import storiesIndex from 'virtual:stories-index'
import { cmsLive, liveAuthors, liveStories, rememberStory } from '../cms/store'
import { withSponsoredRel } from '../lib/affiliate'
import { publicPath } from '../lib/paths'
import type { HomeBoot } from '../seo/boot'
import authorsJson from './authors.json'
import exactdnMap from './exactdn-map.json' with { type: 'json' }
import pagesJson from './pages.json'
import shopProductsJson from './shop-products.json'

const LEGACY = exactdnMap as Record<string, string>

export function asset(path: string, _width?: number) {
  const key = `https://ektfasqp5br.exactdn.com/wp-content/uploads/${path}`
  if (LEGACY[key]) return LEGACY[key]
  const parts = path.split('/').filter(Boolean)
  const file = (parts.pop() || 'image').replace(/-\d+x\d+(?=\.[^.]+$)/, '')
  const base = file.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '-')
  const prefix = parts.join('-')
  return `/posts/${prefix ? `${prefix}-` : ''}${base}.webp`
}

export const logoSrc = asset('2022/06/TIGG-Logo_2T-web-1-1.png', 240)

export type Category =
  | 'Look Books'
  | 'Trends'
  | '2Dos'
  | 'Travel'
  | 'Food'
  | 'Beauty'
  | 'Style'
  | 'Health and Wellness'
  | 'Gift Guide'

export type Story = {
  slug: string
  title: string
  dek: string
  category: Category
  date: string
  /** Full URL, site path, or Railway bucket key (resolved with mediaUrl). */
  image?: string
  imageAlt?: string
  metaDescription?: string
  /** Document title. The visible H1 stays `title`. */
  seoTitle?: string
  faq?: { question: string; answer: string }[]
  author: string
  body?: string[]
  bodyHtml?: string
  imageCredit?: string
  isRecipe?: boolean
  ingredients?: string[]
}

export function formatImageCredit(credit: string) {
  const handle = credit.trim().replace(/^@/, '')
  return handle ? `@${handle}` : ''
}

export type Product = {
  slug: string
  name: string
  brand: string
  image: string
  href: string
  price?: string
}


export type PressItem = {
  title: string
  url: string
  image: string
  outlet: string
  date?: string
  excerpt?: string
}

export type PageMeta = {
  kicker?: string
  title: string
  dek?: string
  subtitle?: string
}

export type AboutBlock =
  | { type: 'hero'; image: string }
  | { type: 'text'; text: string }
  | { type: 'image'; image: string }
  | { type: 'split'; text: string; image: string; imagePosition: 'left' | 'right' }

export type LegalPage = {
  title: string
  bodyHtml: string
}

export type SitePages = {
  press: PageMeta & { items: PressItem[] }
  contact: PageMeta & { channels: { label: string; email: string }[] }
  newsletter: PageMeta & { subtitle: string; dek: string; image: string }
  about: {
    blocks: AboutBlock[]
    instagram: string
    instagramLabel: string
    lookbooksPromo?: string
  }
  contributors: PageMeta
  shop: PageMeta
  closet: PageMeta
  legal: Record<string, LegalPage>
}

export const pages = pagesJson as SitePages
export type NavLink = {
  label: string
  href: string
  children?: readonly { label: string; href: string }[]
}

export const navLinks: NavLink[] = [
  { label: 'Look Books', href: '/look-books/' },
  { label: 'Trends', href: '/trends/' },
  {
    label: '2Dos',
    href: '/2dos/',
    children: [
      { label: 'Health and Wellness', href: '/wellness/' },
      { label: 'Travel', href: '/travel/' },
      { label: 'Food', href: '/food/' },
    ],
  },
  { label: 'Shop', href: '/closet/' },
]

export const socials = [
  { label: 'Instagram', short: 'IG', href: 'https://www.instagram.com/theitgirlguideofficial/' },
  { label: 'TikTok', short: 'TT', href: 'https://www.tiktok.com/@theitgirlguide' },
  { label: 'Pinterest', short: 'PT', href: 'https://www.pinterest.com/theitgirlguideofficial/' },
  { label: 'Twitter', short: 'X', href: 'https://twitter.com/theitgirlguide_' },
] as const

export const footerCols = [
  {
    head: 'Read',
    links: [
      { label: 'Look Books', href: '/look-books/' },
      { label: 'Trends', href: '/trends/' },
      { label: '2Dos', href: '/2dos/' },
      { label: 'Health and Wellness', href: '/wellness/' },
      { label: 'Travel', href: '/travel/' },
      { label: 'Food', href: '/food/' },
    ],
  },
  {
    head: 'Shop',
    links: [
      { label: 'Shop My Closet', href: '/closet/' },
      { label: "Sky's Amazon Store", href: 'https://www.amazon.com/shop/skyhaarsma' },
    ],
  },
  {
    head: 'TIGG',
    links: [
      { label: 'About Sky', href: '/about/' },
      { label: 'Contributors', href: '/contributors/' },
      { label: 'Press', href: '/press/' },
      { label: 'Newsletter', href: 'https://theitgirlguide.com/newsletter/' },
      { label: 'Contact', href: '/contact/' },
    ],
  },
] as const

export type AuthorSocial = {
  type: 'instagram' | 'facebook' | 'pinterest' | 'tiktok' | 'twitter'
  url: string
}

export type Author = {
  slug: string
  name: string
  photo?: string
  bio: string
  email?: string
  website?: string
  socials?: AuthorSocial[]
}

const staticAuthors: Author[] = authorsJson as Author[]

export function allAuthors(): Author[] {
  if (cmsLive()) return liveAuthors()
  return staticAuthors
}

export function getAuthor(name: string) {
  return allAuthors().find((author) => author.name === name)
}

export function storiesByAuthor(name: string) {
  return storiesByDate().filter((story) => story.author === name)
}

export function storiesByAuthorSlug(slug: string) {
  const author = allAuthors().find((person) => person.slug === slug)
  return author ? storiesByAuthor(author.name) : []
}

function stripImportedProductLinks(html: string) {
  const next = html.replace(
    /\s*<div class="wp-block-column">\s*<figure class="wp-block-image">\s*<a href="[^"]+"[^>]*>\s*<img [^>]+>\s*<\/a>\s*<\/figure>\s*<\/div>/gi,
    '',
  )
  return next === html ? html : next.replace(/\s*<\/div>\s*$/, '\n')
}

function titledIframes(html: string) {
  return html.replace(/<iframe\b([^>]*)>/gi, (tag, attrs: string) => {
    if (/\stitle\s*=/i.test(attrs)) return tag
    const src = attrs.match(/\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i)
    const url = src?.[1] ?? src?.[2] ?? ''
    const title = /youtube|youtu\.be/i.test(url)
      ? 'YouTube video'
      : /vimeo/i.test(url)
        ? 'Vimeo video'
        : /instagram/i.test(url)
          ? 'Instagram embed'
          : 'Embedded video'
    return `<iframe${attrs} title="${title}">`
  })
}

function hydrateStory(story: Story): Story {
  const image = story.image?.replace(/\.(jpe?g|png)$/i, '.webp')
  if (!story.bodyHtml) return { ...story, image }
  return {
    ...story,
    image,
    bodyHtml: withSponsoredRel(titledIframes(stripImportedProductLinks(story.bodyHtml))),
  }
}

const staticStories: Story[] = (storiesIndex as Story[]).map(hydrateStory)

export function allStories(): Story[] {
  if (cmsLive()) return liveStories().map(hydrateStory)
  return staticStories
}

export const closetBanner = asset('2023/01/shop-my-closet-5.jpg', 1600)

export type HomeCategory = 'Look Books' | 'Trends' | '2Dos'

export const homeCategories: { name: HomeCategory; href: string }[] = [
  { name: 'Look Books', href: '/look-books/' },
  { name: 'Trends', href: '/trends/' },
  { name: '2Dos', href: '/2dos/' },
]

export const products = shopProductsJson as Product[]

export const cityGuides = [
  { name: 'Los Angeles', image: asset('2026/05/TIGG-city-guides-LA.jpg'), href: '/travel/' },
  { name: 'Orange County', image: asset('2026/05/TIGG-city-guides-OCp.jpg'), href: '/travel/' },
  { name: 'New York City', image: asset('2026/05/TIGG-city-guides-NYC.jpg'), href: '/travel/' },
  { name: 'London', image: asset('2026/05/TIGG-city-guides-london.jpg'), href: '/travel/' },
  { name: 'Amsterdam', image: asset('2026/05/TIGG-city-guides-Amsterdam.jpg'), href: '/travel/' },
  { name: 'Toronto', image: asset('2026/05/TIGG-city-guides-Tor.jpg'), href: '/travel/' },
]

export const categoryRoutes: Record<string, string> = {
  'Look Books': '/look-books/',
  Trends: '/trends/',
  '2Dos': '/2dos/',
  Travel: '/travel/',
  Food: '/food/',
  Beauty: '/beauty/',
  Style: '/trends/',
  'Health and Wellness': '/wellness/',
  'Gift Guide': '/trends/',
}

const archiveCategories: Record<string, Category[]> = {
  '/look-books': ['Look Books'],
  '/trends': ['Trends', 'Style', 'Gift Guide'],
  '/2dos': ['2Dos', 'Travel', 'Food', 'Health and Wellness'],
  '/travel': ['Travel'],
  '/food': ['Food'],
  '/beauty': ['Beauty'],
  '/wellness': ['Health and Wellness'],
}

export const archiveMeta: Record<
  string,
  { title: string; kicker: string; dek: string }
> = {
  '/look-books': {
    title: 'Look Books',
    kicker: 'The edit',
    dek: 'outfits with somewhere to go.',
  },
  '/trends': {
    title: 'Trends',
    kicker: 'What is happening',
    dek: 'the things people are actually talking about.',
  },
  '/2dos': {
    title: '2Dos',
    kicker: 'Malibu 2 Paris',
    dek: 'where to eat, drink, walk, and linger.',
  },
  '/travel': {
    title: 'Travel',
    kicker: 'From Malibu 2 Paris',
    dek: 'city guides, 48 hours, and the jacket you buy on the way.',
  },
  '/food': {
    title: 'Food',
    kicker: 'A table for two',
    dek: 'the meals I cannot stop thinking about.',
  },
  '/beauty': {
    title: 'Beauty',
    kicker: 'The vanity',
    dek: 'undone on purpose.',
  },
  '/wellness': {
    title: 'Health and Wellness',
    kicker: 'Green juice girl',
    dek: 'smoothies, pilates, and the rest of the ritual.',
  },
  '/stories': {
    title: 'All stories',
    kicker: 'The archive',
    dek: 'every TIGG post, newest first.',
  },
}

export function getStory(slug: string) {
  return allStories().find((story) => story.slug === slug)
}

let fullStories: Story[] | undefined

export type LoadedStory = { story: Story; related: Story[] }

export async function loadFullStory(slug: string): Promise<LoadedStory | undefined> {
  const live = liveStories().find((story) => story.slug === slug)
  if (live?.bodyHtml || live?.body?.length) {
    const story = hydrateStory(live)
    return { story, related: relatedStories(story) }
  }

  try {
    const res = await fetch(`/api/public/story/${encodeURIComponent(slug)}`)
    if (res.ok) {
      const data = (await res.json()) as { story: Story; related?: Story[]; author?: Author | null }
      const story = hydrateStory(data.story)
      const related = (data.related ?? []).map((item) => hydrateStory(item))
      rememberStory(story, { related, author: data.author })
      return { story, related }
    }
    if (res.status === 404) return undefined
  } catch {
    // Static fallback below covers dev without the API.
  }

  if (!fullStories) {
    const { default: storiesJson } = await import('./stories.json')
    fullStories = (storiesJson as Story[]).map(hydrateStory)
  }
  const story = fullStories.find((item) => item.slug === slug)
  if (!story) return undefined
  return { story, related: relatedStories(story) }
}

export function storiesForPath(path: string) {
  if (path === '/stories') return storiesByDate()
  const cats = archiveCategories[path]
  if (!cats) return []
  return storiesByDate().filter((story) => cats.includes(story.category))
}

export function homeCategory(story: Story): HomeCategory {
  if (story.category === 'Look Books') return 'Look Books'
  if (
    story.category === '2Dos' ||
    story.category === 'Travel' ||
    story.category === 'Food' ||
    story.category === 'Health and Wellness'
  ) {
    return '2Dos'
  }
  return 'Trends'
}

function parseStoryDate(date: string) {
  const [month, day, year] = date.split(' / ').map((part) => Number(part))
  return Date.UTC(2000 + year, month - 1, day)
}

export function storiesByDate() {
  return [...allStories()].sort((a, b) => parseStoryDate(b.date) - parseStoryDate(a.date))
}

export function latestStory() {
  return storiesByDate()[0]
}

function shuffleStories(items: Story[]) {
  const next = [...items]
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[next[i], next[j]] = [next[j], next[i]]
  }
  return next
}

function pickSpotlightStories(items: Story[], name: HomeCategory, count = 4) {
  return shuffleStories(items.filter((story) => homeCategory(story) === name)).slice(0, count)
}

function pickMixedStories(items: Story[], count: number) {
  const buckets: Record<HomeCategory, Story[]> = {
    'Look Books': [],
    Trends: [],
    '2Dos': [],
  }
  for (const story of shuffleStories(items)) {
    buckets[homeCategory(story)].push(story)
  }

  const picked: Story[] = []
  const order = homeCategories.map((category) => category.name)
  let turns = 0
  while (picked.length < count && turns < count * order.length) {
    const bucket = buckets[order[turns % order.length]]
    turns += 1
    const next = bucket.shift()
    if (next) picked.push(next)
  }

  return shuffleStories(picked)
}

export function serializeHomeBoot(feed: ReturnType<typeof homeFeed>): HomeBoot | undefined {
  if (!feed.hero) return undefined
  return {
    hero: feed.hero.slug,
    hottest: feed.hottest.map((story) => story.slug),
    categorySpotlights: feed.categorySpotlights.map((spotlight) => ({
      name: spotlight.name,
      href: publicPath(spotlight.href),
      image: spotlight.image.slug,
      lead: spotlight.lead.slug,
      list: spotlight.list.map((story) => story.slug),
    })),
    more: feed.more.map((story) => story.slug),
  }
}

export function resolveHomeBoot(boot: HomeBoot) {
  const bySlug = new Map(allStories().map((story) => [story.slug, story]))
  const pick = (slug: string) => bySlug.get(slug)
  const hero = pick(boot.hero)
  if (!hero) return null
  const hottest = boot.hottest.map(pick).filter((story): story is Story => Boolean(story))
  const categorySpotlights = boot.categorySpotlights.flatMap((spotlight) => {
    const image = pick(spotlight.image)
    const lead = pick(spotlight.lead)
    const list = spotlight.list.map(pick).filter((story): story is Story => Boolean(story))
    if (!image || !lead) return []
    return [{ name: spotlight.name, href: spotlight.href, image, lead, list }]
  })
  const more = boot.more.map(pick).filter((story): story is Story => Boolean(story))
  return { hero, hottest, categorySpotlights, more }
}

export function homeFeed() {
  const ranked = storiesByDate()
  const hero = ranked[0]
  const used = new Set<string>(hero ? [hero.slug] : [])

  const categorySpotlights = homeCategories.flatMap((category) => {
    const available = ranked.filter((story) => !used.has(story.slug))
    const pool = pickSpotlightStories(available, category.name)
    if (pool.length < 4) return []

    pool.forEach((story) => used.add(story.slug))
    return [
      {
        name: category.name,
        href: category.href,
        image: pool[0],
        lead: pool[1],
        list: pool.slice(2, 4),
      },
    ]
  })

  const unusedIn = (name: HomeCategory) =>
    ranked.filter((story) => homeCategory(story) === name && !used.has(story.slug)).length
  const reserve = Object.fromEntries(
    homeCategories.map((category) => [category.name, Math.min(5, unusedIn(category.name))]),
  ) as Record<HomeCategory, number>

  const hottest: Story[] = []
  for (const story of ranked) {
    if (hottest.length === 8) break
    if (used.has(story.slug)) continue
    const category = homeCategory(story)
    if (unusedIn(category) - 1 < reserve[category]) continue
    hottest.push(story)
    used.add(story.slug)
  }

  const remaining = ranked.filter((story) => !used.has(story.slug))

  return {
    hero,
    hottest,
    categorySpotlights,
    more: pickMixedStories(remaining, 8),
  }
}

export function relatedStories(story: Story, count = 4) {
  return storiesByDate()
    .filter((item) => item.slug !== story.slug && item.category === story.category)
    .slice(0, count)
}

function fold(value: string) {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

export function searchStories(query: string) {
  const q = fold(query.trim())
  if (!q) return []
  return allStories().filter((story) => fold(`${story.title} ${story.dek} ${story.category}`).includes(q))
}

export function issueDate() {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date())
}
